"use client";

import { useRef, useState } from "react";
import { toTsv } from "@/components/tool-kit/charts/figure";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  SummaryChart,
  TrafficChart,
} from "@/components/tools/research-discovery/rob-chart";
import type { RiskOfBiasPlotGeneratorStrings } from "@/components/tools/risk-of-bias-plot-generator";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";
import {
  layoutSummary,
  layoutTrafficLight,
  type RobLabels,
} from "@/lib/tool-kit/research-discovery/rob-layout";
import {
  hasAllWeights,
  levelsUsed,
  overallOf,
  parseRobTable,
  type RobIssue,
  type RobLevel,
  type RobPaletteId,
  type RobStudy,
  type RobToolId,
  robTool,
  type SummaryBar,
  summaryBars,
} from "@/lib/tool-kit/research-discovery/rob-plot";

type Strings = RiskOfBiasPlotGeneratorStrings;
export type RobPlotId = "traffic" | "summary";

export interface RobOptions {
  tool: RobToolId;
  plot: RobPlotId;
  palette: RobPaletteId;
  title: string;
  overall: boolean;
  weighted: boolean;
}

type LevelNames = Readonly<Record<RobLevel, string>>;
interface TableData {
  head: string[];
  rows: string[][];
}

function levelNames(strings: Strings, tool: RobToolId): LevelNames {
  return tool === "rob2" ? strings.levelsRob2 : strings.levelsRobinsi;
}

function domainNames(strings: Strings, tool: RobToolId): readonly string[] {
  return tool === "rob2" ? strings.domainsRob2 : strings.domainsRobinsi;
}

function figureLabels(
  strings: Strings,
  { tool, title }: RobOptions,
  weighted: boolean
): RobLabels {
  const names = domainNames(strings, tool);
  return {
    title,
    studyHeader: strings.studyHeader,
    domainHeaders: names.map((_, index) => `D${index + 1}`),
    overallHeader: strings.overallHeader,
    keyHeading: strings.keyHeading,
    domainKeys: names.map((name, index) => `D${index + 1}: ${name}`),
    legendHeading: strings.legendHeading,
    levelLabels: levelNames(strings, tool),
    domainNames: names,
    overallName: strings.overallName,
    axisTitle: weighted ? strings.axisWeight : strings.axisStudies,
  };
}

function issueText(strings: Strings, issue: RobIssue): string {
  if (issue.kind === "columns") {
    return t(strings.issueColumns, {
      line: String(issue.line),
      expected: String(issue.expected),
    });
  }
  const template =
    issue.kind === "judgement" ? strings.issueJudgement : strings.issueWeight;
  return t(template, { line: String(issue.line), value: issue.value });
}

function trafficTable(
  strings: Strings,
  options: RobOptions,
  studies: readonly RobStudy[]
): TableData {
  const names = levelNames(strings, options.tool);
  const count = robTool(options.tool).domainCount;
  const head = [
    strings.studyColumn,
    ...Array.from({ length: count }, (_, index) => `D${index + 1}`),
  ];
  if (options.overall) {
    head.push(strings.overallHeader);
  }
  const rows = studies.map((study) => {
    const row = [study.name, ...study.domains.map((level) => names[level])];
    if (options.overall) {
      row.push(names[overallOf(study)]);
    }
    return row;
  });
  return { head, rows };
}

function summaryTable(
  strings: Strings,
  options: RobOptions,
  bars: readonly SummaryBar[],
  used: readonly RobLevel[],
  percent: (share: number) => string
): TableData {
  const names = levelNames(strings, options.tool);
  const domains = domainNames(strings, options.tool);
  const head = [strings.rowColumn, ...used.map((level) => names[level])];
  const rows = bars.map((bar) => [
    bar.domain === "overall" ? strings.overallName : domains[bar.domain],
    ...used.map((level) =>
      percent(bar.segments.find((s) => s.level === level)?.share ?? 0)
    ),
  ]);
  return { head, rows };
}

/** The parsed table's problems, the figure, its downloads and a text alternative. */
export function RobResults({
  strings,
  options,
  text,
}: {
  strings: Strings;
  options: RobOptions;
  text: string;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const holder = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const { tool, plot, palette, title, overall, weighted } = options;
  const { studies, issues } = parseRobTable(tool, text);
  const canWeight = hasAllWeights(studies);
  const useWeights = weighted && canWeight;
  const names = levelNames(strings, tool);
  const used = levelsUsed(tool, studies, overall);
  const percent = (share: number) =>
    fmt(share, { style: "percent", maximumFractionDigits: 0 });
  const bars = summaryBars(tool, studies, { overall, weighted: useWeights });
  const labels = figureLabels(strings, options, useWeights);
  const plotName =
    plot === "traffic" ? strings.trafficName : strings.summaryName;
  const frame = {
    id: "robp-figure",
    palette,
    label: t(strings.figureLabel, { plot: plotName }),
    description: t(strings.figureDescription, {
      plot: plotName,
      count: fmt(studies.length),
      tool: tool === "rob2" ? strings.rob2Name : strings.robinsiName,
      judgements: t(strings.figureJudgements, {
        levels: used.map((level) => names[level]).join(", "),
      }),
    }),
  };
  const table =
    plot === "traffic"
      ? trafficTable(strings, options, studies)
      : summaryTable(strings, options, bars, used, percent);
  const baseName = chartFilename(
    title,
    plot === "traffic" ? "risk-of-bias-traffic-light" : "risk-of-bias-summary"
  );
  const shown = issues.slice(0, 5);
  const hidden = issues.length - shown.length;

  function svg() {
    return holder.current?.querySelector("svg") ?? null;
  }

  function saveSvg() {
    const element = svg();
    if (element) {
      downloadSvg(`${baseName}.svg`, element);
    }
  }

  async function savePng() {
    const element = svg();
    if (!element) {
      return;
    }
    try {
      setFailed(false);
      await downloadPng(`${baseName}.png`, element, { scale: 2 });
    } catch {
      setFailed(true);
    }
  }

  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.resultHeading}</h2>
      {shown.map((issue) => (
        <ErrorNotice
          key={`${issue.kind}-${issue.line}-${issue.kind === "columns" ? "" : issue.value}`}
        >
          {issueText(strings, issue)}
        </ErrorNotice>
      ))}
      {hidden > 0 ? (
        <Notice>
          {tPlural(strings.moreIssues, locale, hidden, { count: fmt(hidden) })}
        </Notice>
      ) : null}
      {studies.length === 0 ? (
        <Notice>{strings.enterPrompt}</Notice>
      ) : (
        <>
          <p className={LABEL}>
            {tPlural(strings.studiesRead, locale, studies.length, {
              count: fmt(studies.length),
            })}
          </p>
          {plot === "summary" && weighted && !canWeight ? (
            <Notice>{strings.weightsMissing}</Notice>
          ) : null}
          {overall && studies.some((study) => study.overall === null) ? (
            <Notice>{strings.overallDerived}</Notice>
          ) : null}
          <div
            className="overflow-x-auto rounded-lg border border-border bg-white"
            ref={holder}
          >
            {plot === "traffic" ? (
              <TrafficChart
                frame={frame}
                layout={layoutTrafficLight(
                  robTool(tool),
                  studies,
                  labels,
                  used,
                  { overall }
                )}
              />
            ) : (
              <SummaryChart
                frame={frame}
                layout={layoutSummary(bars, labels, used, percent)}
                percent={percent}
              />
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              className={BUTTON_SECONDARY}
              onClick={saveSvg}
              type="button"
            >
              {strings.downloadSvg}
            </button>
            <button
              className={BUTTON_SECONDARY}
              onClick={savePng}
              type="button"
            >
              {strings.downloadPng}
            </button>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyData}
              text={toTsv([table.head, ...table.rows])}
            />
          </div>
          {failed ? <ErrorNotice>{strings.exportFailed}</ErrorNotice> : null}
          <details>
            <summary className={`${LABEL} cursor-pointer`}>
              {strings.showTable}
            </summary>
            <div className="mt-3">
              <DataTable head={table.head} rows={table.rows} />
            </div>
          </details>
        </>
      )}
    </section>
  );
}
